/**
 * Session courante, vue du frontend.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CONTRAT ATTENDU DU MODULE `auth` (ARCHITECTURE §4, §8)
 *
 *   POST /api/auth/login                { email, motDePasse }  → session staff
 *   POST /api/auth/logout               —                      → { ok: true }
 *   GET  /api/auth/moi                  —                      → session | 401
 *   POST /api/auth/magic-link           { email }              → { ok: true }   (réponse neutre, POR-01)
 *   POST /api/auth/magic-link/verifier  { token }              → session abonné
 *
 * Forme d’une session :
 *   { "sujet": "staff",  "utilisateur": { id, email, nom, role } }
 *   { "sujet": "abonne", "personne":    { id, nom, prenom, email } }
 *
 * `lireSession` reste tolérante (elle accepte l’absence de `sujet` et le déduit)
 * pour que le frontend ne casse pas sur un détail de sérialisation ; le schéma
 * Zod qui fait foi vivra dans `packages/shared/src/dto/auth.ts`.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  estRoleUtilisateur,
  droitsMetierSchema,
  type DroitMetier,
  type RoleUtilisateur,
} from '@tb/shared'

/** Membre du personnel connecté (SPEC §4). */
export interface UtilisateurStaff {
  readonly id: string
  readonly email: string
  readonly nom: string
  readonly role: RoleUtilisateur
  readonly droitsMetier?: DroitMetier[] | null
}

/** Abonné connecté au portail par lien magique (POR-01). */
export interface AbonnePortail {
  readonly id: string
  readonly nom: string
  readonly prenom: string
  readonly email: string | null
}

export type Session =
  | { readonly sujet: 'staff'; readonly utilisateur: UtilisateurStaff }
  | { readonly sujet: 'abonne'; readonly personne: AbonnePortail }

/* -------------------------------------------------------------------------- */
/* Lecture défensive de la réponse                                            */
/* -------------------------------------------------------------------------- */

function objet(valeur: unknown): Record<string, unknown> | null {
  return typeof valeur === 'object' && valeur !== null ? (valeur as Record<string, unknown>) : null
}

function texte(valeur: unknown, defaut = ''): string {
  return typeof valeur === 'string' ? valeur : defaut
}

/** `null` si le corps ne décrit aucune session exploitable. */
export function lireSession(donnees: unknown): Session | null {
  const racine = objet(donnees)
  if (racine === null) return null

  const brutStaff = objet(racine.utilisateur)
  if (brutStaff !== null && estRoleUtilisateur(brutStaff.role)) {
    return {
      sujet: 'staff',
      utilisateur: {
        id: texte(brutStaff.id),
        email: texte(brutStaff.email),
        nom: texte(brutStaff.nom, texte(brutStaff.email)),
        role: brutStaff.role,
        ...(brutStaff.droitsMetier !== undefined
          ? {
              droitsMetier: droitsMetierSchema.safeParse(brutStaff.droitsMetier).success
                ? droitsMetierSchema.parse(brutStaff.droitsMetier)
                : [],
            }
          : {}),
      },
    }
  }

  const brutAbonne = objet(racine.personne)
  if (brutAbonne !== null) {
    const email = brutAbonne.email
    return {
      sujet: 'abonne',
      personne: {
        id: texte(brutAbonne.id),
        nom: texte(brutAbonne.nom),
        prenom: texte(brutAbonne.prenom),
        email: typeof email === 'string' ? email : null,
      },
    }
  }

  return null
}

/** Nom affiché dans l’en-tête. */
export function nomAffiche(session: Session): string {
  return session.sujet === 'staff'
    ? session.utilisateur.nom
    : `${session.personne.prenom} ${session.personne.nom}`.trim()
}
